/* eslint-disable react/no-multi-comp */
import {useState, useEffect} from 'react';
import classNames from 'classnames';

import Modal from '../_modal';
import PageMonitor from './_page_monitor';
import useLocalStorage from '../../hooks/useLocalStorage';
import {isValidId} from '../../modules/utils';
import {rivalMonitorsGet, rivalMonitorCreate, rivalMonitorDelete} from '../../modules/api/monitors';

const oneHourInMs = 60 * 60 * 1000;

const PageMonitors = ({rival, onClose}, {utils, appData}) => {
  const [newMonitors, setNewMonitors] = useState([]);
  const [monitors, setMonitors] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [deletedMonitors, setDeletedMonitors] = useLocalStorage('deleted-page-monitors', []);
  const {id: rivalId, iconUrl, name} = rival || {};

  useEffect(() => {
    rivalMonitorsGet(rivalId)
      .then(data => {
        const deletedMonitorIds = deletedMonitors.map(({id}) => id);

        setMonitors(data.filter(monitor => !deletedMonitorIds.includes(monitor.id)));
      }).catch(() => {
        setError('Whoops! Something unexpected happened.');
      }).finally(() => {
        setLoading(false);
      });
  }, []);

  const handleDeleteMonitor = (monitor, verify = false) => {
    const hardDeleteMonitor = () => {
      const {isNew} = monitor;

      if(isNew) {
        return setNewMonitors(nm => nm.filter(m => m.id !== monitor.id));
      }

      PageMonitors.deletedMonitors.add(monitor);
      setMonitors(mons => mons.filter(m => m.id !== monitor.id));
    };

    if(verify) {
      return utils.dialog.confirm({
        message: 'Are you sure you want to delete this monitor?',
        okCallback: hardDeleteMonitor
      });
    }

    hardDeleteMonitor();
  };

  const handleSaveMonitor = (monitor, value) => {
    const {isNew} = monitor;

    if(isNew) {
      return setNewMonitors(nm => nm.map(m => ((m.id === monitor.id) ? {...monitor, url: value} : m)));
    }
  };

  const handleAddNewMonitor = () => {
    setNewMonitors(nm => [{
      id: `new_monitor_${PageMonitors.nextNewMonitorId++}`,
      isNew: true,
      type: 'page'
    }].concat(nm));
  };

  const cleanupAndClose = () => {
    PageMonitors.deletedMonitors.clear();
    onClose();
  };

  // TODO: add check for edited existing monitors
  const haveOutstandingEdits = () => newMonitors.length ||
    PageMonitors.deletedMonitors.size ||
    monitors.find(m => m.editedUrl && (m.editedUrl !== m.url));

  const alertErrorConditionsIfNecessary = ({rejectDeletionsCount, rejectNewCount}) => {
    const errorMessagePrefix = 'Oops, something went wrong. Some of the ';
    const errorMessageSuffix = 'did not take hold. Please try again.';

    if(rejectDeletionsCount && rejectNewCount) {
      utils.dialog.alert(`${errorMessagePrefix}deletions and new monitors ${errorMessageSuffix}`);
    }
    else if(rejectDeletionsCount) {
      utils.dialog.alert(`${errorMessagePrefix}deletions ${errorMessageSuffix}`);
    }
    else if(rejectNewCount) {
      utils.dialog.alert(`${errorMessagePrefix}new monitors ${errorMessageSuffix}`);
    }
  };

  const saveChanges = () => {
    const deleteActions = [];
    const deletedIds = [];
    const newActions = [];

    for(const deletedMonitor of PageMonitors.deletedMonitors) {
      const {id} = deletedMonitor;

      if(isValidId(id)) {
        deleteActions.push(rivalMonitorDelete({id}));
        deletedIds.push(id);
      }
    }

    for(const newMonitor of newMonitors) {
      const {url} = newMonitor;

      if(url) {
        newActions.push(rivalMonitorCreate({rivalId, url}));
      }
    }

    const apiActions = deleteActions.concat(newActions);

    if(!apiActions.length) {
      return cleanupAndClose();
    }

    setSaving(true);

    Promise.allSettled(apiActions)
      .then(values => {
        const now = new Date().getTime();
        const freshDeletedMonitors = deletedMonitors.filter(({expiry}) => expiry && (new Date(expiry).getTime() - now) > 0);
        let rejectDeletionsCount = 0;
        let rejectNewCount = 0;

        values.forEach(({status}, i) => {
          const isDeletion = i < deleteActions.length;

          if(status === 'fulfilled') {
            if(isDeletion) {
              // save deletion to local storage so we don't reveal it while the background deletion job is pending
              freshDeletedMonitors.push({id: deletedIds[i], expiry: now + oneHourInMs});
            }
          }
          else if(isDeletion) { // we have a rejection
            rejectDeletionsCount += 1;
          }
          else {
            rejectNewCount += 1;
          }
        });

        setDeletedMonitors(freshDeletedMonitors);
        alertErrorConditionsIfNecessary({rejectDeletionsCount, rejectNewCount});
        cleanupAndClose();
      });
  };

  // invalid and blank urls are not passed up to set the url field so if the url field doesn't exist, it's considered invalid.
  const allValid = () => !newMonitors.find(monitor => !monitor.url);

  const handleAllDone = () => {
    if(!haveOutstandingEdits()) {
      return cleanupAndClose();
    }

    if(!allValid()) {
      return utils.dialog.confirm({
        message: 'Some of the URLs are invalid or blank and will be ignored if you continue and save.',
        okCallback: saveChanges,
        buttonOk: 'Save & Exit'
      });
    }

    saveChanges();
  };

  const handleOnClose = () => {
    if(haveOutstandingEdits()) {
      return utils.dialog.confirm({
        message: 'Are you sure you want to close the dialog and lose all your changes?',
        okCallback: cleanupAndClose,
        buttonOk: 'Yes, Close It'
      });
    }

    cleanupAndClose();
  };

  const busy = loading || saving;

  const disableAddNew = newMonitors.find(monitor => !monitor.url);

  const header = (
    <div className="page-monitors_header">
      <div className="page-monitors_header-rival-status">
        <div>
          <img className="header-image" src={iconUrl} />
          <h1 className="header-text">
            <span className="header-text-name">{name}</span>
            <span> - Page Monitors</span>
          </h1>
        </div>
        <p className={classNames('header-error', {'header-error-visible': error})}>{error}</p>
      </div>
      <div className="page-monitors_header_list-header">
        <div className="page-monitors-title">
          <h2 className="page-monitors-title-text">web pages</h2>
        </div>
        <button
          className={classNames('btn button button--small button-add', {invisible: busy})}
          onClick={handleAddNewMonitor}
          data-testid="add-new-button"
          data-tracking-id="monitors-add-new-button"
          disabled={disableAddNew}>
          add new
        </button>
      </div>
    </div>
  );

  const footer = (
    <div className="page-monitors_footer">
      <p className="page-monitors_footer-text">
        Note: <strong>Klue is already monitoring news source, press releases, and blog posts for you</strong>. If you want to know when a webpage related to this Board has changed, add the URL here. Visit the <a href={`//${appData.helpDocsHost}/article/uv16bhuld4-how-do-i-start-monitoring-a-page`} target="_blank" rel="noreferrer nofollow">Help Docs</a> to learn more.
      </p>
      <button
        className={classNames('btn button button--medium button-done', {'button--disabled': saving})}
        onClick={handleAllDone}
        data-testid="all-done-button"
        disabled={saving}>
        {saving ? 'Saving...' : 'Save & exit'}
      </button>
    </div>
  );

  const infoName = name ? (<strong>{name}</strong>) : 'your board';
  const allMonitors = newMonitors.concat(monitors);

  return (
    <Modal
      header={false}
      footer={false}
      padded={false}
      basic={true}
      onClose={handleOnClose}
      closeOnOutsideClick={false}>
      <div className="page-monitors">
        <div className="page-monitors_content">
          {header}
          {busy
            ? (
              <div className="page-monitors_busy">
                <div className="upload-indicator upload-indicator-centered" />
              </div>
            )
            : allMonitors.length
              ? (
                <div className="page-monitors_list" data-testid="page-monitors-list">
                  {allMonitors.map(monitor => {
                    const {id} = monitor;

                    return (
                      <PageMonitor
                        key={id}
                        monitor={monitor}
                        onDeleteHit={handleDeleteMonitor}
                        onSaveHit={handleSaveMonitor} />
                    );
                  })}
                </div>
              )
              : (
                <div className="page-monitors_info" data-testid="page-monitors_info">
                  <p>Add some pages related to {infoName} to start tracking changes.</p>
                  <p><small>We’ll let you know when the page is updated.</small></p>
                </div>
              )
          }
          {footer}
        </div>
      </div>
    </Modal>
  );
};

PageMonitors.nextNewMonitorId = 0;
PageMonitors.deletedMonitors = new Set();

PageMonitors.propTypes = {
  rival: PropTypes.object,
  onClose: PropTypes.func.isRequired
};

PageMonitors.defaultProps = {
  rival: null
};

PageMonitors.contextTypes = {
  utils: PropTypes.object.isRequired,
  appData: PropTypes.object.isRequired
};

export default PageMonitors;
